import { z } from "zod";

import { ClinicCallReason } from "@/packages/shared/src/models/clinic-call-reason";
import { STATE_NAMES } from "@/src/lib/us-states";
import {
  isAdwSchema,
  nullableSignaturePageNumberSchema,
  SIGNATURE_PAGE_NUMBER_PROMPT,
} from "@/src/models/common-schema";
import { AdminDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import {
  BODY_PART_PROMPT,
  CLINIC_CALL_REASON_PROMPT,
  ORDERING_PROVIDER_PROMPT,
  SCHEDULING_PRIORITY_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { SchedulingPriority } from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { Patient, patientSchema } from "@/src/models/patient";
import { Practitioner, practitionerSchema } from "@/src/models/practitioner";

export const adminDocumentTypeDisplay: Record<AdminDocumentType, string> = {
  [AdminDocumentType.ACO]: "ACO Consent",
  [AdminDocumentType.APCM]: "APCM Consent",
  [AdminDocumentType.CASE_MANAGER_DOCUMENTS]: "Case Manager Documents (Social Workers, Insurance Case Management, CCM)",
  [AdminDocumentType.CERTIFICATE_OF_MEDICAL_NECESSITY]: "Certificate of Medical Necessity",
  [AdminDocumentType.CREDENTIAL]: "Credentialing Documents",
  [AdminDocumentType.DEATH_CERTIFICATES]: "Death Certificates",
  [AdminDocumentType.DISABILITY_FORMS]: "Disability Forms",
  [AdminDocumentType.DOH_FORMS]: "DOH Illness Report Form",
  [AdminDocumentType.FMLA_FORMS]: "FMLA Forms",
  [AdminDocumentType.HEAD_START_PROGRAM]: "Head Start Program",
  [AdminDocumentType.PATIENT_SHARED_INFORMATION]: "Patient Logs (Blood Pressure/Glucose/Meds)",
  [AdminDocumentType.RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND]:
    "Response from Outside Provider/Record Not Found",
  [AdminDocumentType.RETURN_TO_WORK]: "Return to Work",
  [AdminDocumentType.SCHOOL_NOTES]: "School Notes",
  [AdminDocumentType.RISK_ASSESSMENT]: "Risk Assessment",
  [AdminDocumentType.WORK_OR_SCHOOL_PHYSICAL_FORMS]: "Work or School Physical Forms",
  [AdminDocumentType.ADMIN]: "Admin",
  [AdminDocumentType.PHYSICIAN_ORDER]: "Physician Order",
  [AdminDocumentType.PATIENT_QUESTIONNAIRE]: "Patient Questionnaire",
  [AdminDocumentType.PATIENT_QUESTIONNAIRE_VANDERBILT]: "Patient Questionnaire - Vanderbilt",
  [AdminDocumentType.ADMIN_TRANSITION_CARE]: "Transition of Care",
  [AdminDocumentType.LEAD_FORM]: "Lead Form",
  [AdminDocumentType.OTHER]: "Admin Other",
  [AdminDocumentType.PATIENT_AUTHORIZATION]: "Patient Authorization",
  [AdminDocumentType.PCP_ACKNOWLEDGEMENT_FORM]: "PCP Acknowledgement Form",
  [AdminDocumentType.PFML_FORMS]: "PFML Forms",
  [AdminDocumentType.POLST]: "POLST",
  [AdminDocumentType.SOCIAL_DETERMINANTS]: "Social Determinants",
  [AdminDocumentType.TRANSPORT_FORM]: "Transport Form",
  [AdminDocumentType.WORKERS_COMP]: "L&I / Workers' Comp",
};

export const adminDocumentTypeSchema = z.nativeEnum(AdminDocumentType);

export enum DisabilityDurationType {
  SHORT_TERM = "short term",
  LONG_TERM = "long term",
}

export enum ClaimType {
  MVA = "MVA",
}

const baseAdminFields = {
  patient: patientSchema,
  receivingLocation: locationSchema.nullable(),
};

// Lead forms need full patient information (address, phone, demographics, etc.)
const leadFormAdminFields = {
  ...baseAdminFields,
  patient: patientSchema,
};

const extendedAdminSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  companyName: z.string().nullable(),
  documentType: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  clinicCallReason: z.nativeEnum(ClinicCallReason).nullable().describe(CLINIC_CALL_REASON_PROMPT),
  equipmentType: z.string().nullable().describe("Equipment Type"),
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  isSigned: z.boolean().nullable().describe("True if the document has been signed by a provider"),
  nameOfDocument: z.string().nullable().describe("Name of Document"),
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
  specialtyOrFacilityName: z.string().nullable().describe("Specialty or Facility Name"),
  equipmentOrSupply: z.string().nullable().describe("Equipment or Supply"),
  nameOfIllness: z.string().nullable().describe("Name of Illness"),
  patientLogType: z.string().nullable().describe("Patient Log Type"),
  signatureRequired: z.boolean().nullable().describe("Is the document requiring a signature?"),
  usStateName: z
    .nativeEnum(STATE_NAMES)
    .nullable()
    .describe(
      "Which US state's Paid Family and Medical Leave program this form is for. Return exactly one of the US state full names (e.g. 'Oregon', 'Washington'). Leave null if the state cannot be determined."
    ),
  signaturePageNumber: nullableSignaturePageNumberSchema.describe(SIGNATURE_PAGE_NUMBER_PROMPT),
  disabilityDurationType: z
    .nativeEnum(DisabilityDurationType)
    .nullable()
    .describe(
      "Whether the disability being claimed or certified on this form is short term or long term. Null if the document does not specify."
    ),
  claimType: z
    .nativeEnum(ClaimType)
    .nullable()
    .describe(
      "Set to 'MVA' when this administrative document is a motor vehicle accident (MVA) insurance claim document " +
        "for service reimbursement (separate from the patient's standard health insurance). Null otherwise."
    ),
  isScored: z.boolean().nullable(),
  location: locationSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
});

const adminCustomFields: Record<AdminDocumentType, Record<string, z.ZodTypeAny>> = {
  [AdminDocumentType.ACO]: extendedAdminSchema.pick({ dateReceived: true, dateSigned: true }).shape,
  [AdminDocumentType.APCM]: extendedAdminSchema.pick({ dateReceived: true, dateSigned: true }).shape,
  [AdminDocumentType.CASE_MANAGER_DOCUMENTS]: extendedAdminSchema.pick({
    isAdw: true,
    dateOfDocument: true,
    nameOfDocument: true,
  }).shape,
  [AdminDocumentType.CERTIFICATE_OF_MEDICAL_NECESSITY]: extendedAdminSchema.pick({
    dateReceived: true,
    equipmentType: true,
    equipmentOrSupply: true,
    receivingProvider: true,
    signatureRequired: true,
  }).shape,
  [AdminDocumentType.CREDENTIAL]: extendedAdminSchema.pick({ dateReceived: true }).shape,
  [AdminDocumentType.DEATH_CERTIFICATES]: extendedAdminSchema.pick({ dateOfEvent: true }).shape,
  [AdminDocumentType.DISABILITY_FORMS]: extendedAdminSchema.pick({
    dateReceived: true,
    documentType: true,
    signatureRequired: true,
    orderingProvider: true,
    receivingProvider: true,
    disabilityDurationType: true,
  }).shape,
  [AdminDocumentType.DOH_FORMS]: extendedAdminSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    nameOfIllness: true,
  }).shape,
  [AdminDocumentType.FMLA_FORMS]: extendedAdminSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    orderingProvider: true,
    receivingProvider: true,
  }).shape,
  [AdminDocumentType.HEAD_START_PROGRAM]: extendedAdminSchema.pick({ dateReceived: true }).shape,
  [AdminDocumentType.PATIENT_SHARED_INFORMATION]: extendedAdminSchema.pick({
    isAdw: true,
    dateReceived: true,
    patientLogType: true,
  }).shape,
  [AdminDocumentType.RESPONSE_FROM_OUTSIDE_PROVIDER_RECORD_NOT_FOUND]: extendedAdminSchema.pick({
    dateReceived: true,
    specialtyOrFacilityName: true,
  }).shape,
  [AdminDocumentType.RETURN_TO_WORK]: extendedAdminSchema.pick({ dateSigned: true }).shape,
  [AdminDocumentType.SCHOOL_NOTES]: extendedAdminSchema.pick({ dateSigned: true }).shape,
  [AdminDocumentType.RISK_ASSESSMENT]: extendedAdminSchema.pick({ dateReceived: true }).shape,
  [AdminDocumentType.WORK_OR_SCHOOL_PHYSICAL_FORMS]: extendedAdminSchema.pick({
    dateSigned: true,
    documentType: true,
    signatureRequired: true,
    orderingProvider: true,
    receivingProvider: true,
  }).shape,
  [AdminDocumentType.ADMIN]: extendedAdminSchema.shape,
  // `bodyPart` is scoped to physician orders rather than living on `extendedAdminSchema`: only an
  // order has an anatomical target worth extracting, and the other admin types share that shape
  // unpruned, so widening it there would add the field to their prompts for no consumer.
  [AdminDocumentType.PHYSICIAN_ORDER]: {
    ...extendedAdminSchema.shape,
    bodyPart: z.string().nullable().describe(BODY_PART_PROMPT),
  },
  [AdminDocumentType.PATIENT_QUESTIONNAIRE]: extendedAdminSchema.shape,
  [AdminDocumentType.PATIENT_QUESTIONNAIRE_VANDERBILT]: extendedAdminSchema.shape,
  [AdminDocumentType.ADMIN_TRANSITION_CARE]: extendedAdminSchema.shape,
  [AdminDocumentType.LEAD_FORM]: extendedAdminSchema.shape,
  [AdminDocumentType.OTHER]: extendedAdminSchema.shape,
  [AdminDocumentType.PATIENT_AUTHORIZATION]: extendedAdminSchema.pick({ dateReceived: true, dateSigned: true }).shape,
  [AdminDocumentType.PCP_ACKNOWLEDGEMENT_FORM]: extendedAdminSchema.pick({ dateOfDocument: true }).shape,
  [AdminDocumentType.PFML_FORMS]: extendedAdminSchema.pick({
    dateReceived: true,
    signatureRequired: true,
    usStateName: true,
    receivingProvider: true,
  }).shape,
  [AdminDocumentType.POLST]: extendedAdminSchema.pick({ isAdw: true, dateReceived: true, dateSigned: true }).shape,
  [AdminDocumentType.SOCIAL_DETERMINANTS]: extendedAdminSchema.pick({ dateReceived: true }).shape,
  [AdminDocumentType.TRANSPORT_FORM]: extendedAdminSchema.pick({ dateReceived: true, companyName: true }).shape,
  [AdminDocumentType.WORKERS_COMP]: extendedAdminSchema.pick({
    dateReceived: true,
    documentType: true,
    signatureRequired: true,
    receivingProvider: true,
  }).shape,
};

const makeAdminSchema = (type: AdminDocumentType, customFields: Record<string, z.ZodTypeAny>) => {
  // Use full patient schema for lead forms, minimal for others
  const patientFields = type === AdminDocumentType.LEAD_FORM ? leadFormAdminFields : baseAdminFields;

  return z.object({
    type: z.literal(type),
    ...patientFields,
    ...customFields,
  });
};

export const adminSchemas: Record<AdminDocumentType, ReturnType<typeof makeAdminSchema>> = (
  Object.values(AdminDocumentType) as AdminDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeAdminSchema(type, adminCustomFields[type]);

    return acc;
  },
  {} as Record<AdminDocumentType, ReturnType<typeof makeAdminSchema>>
);

const adminSchemasArray = (Object.values(AdminDocumentType) as AdminDocumentType[]).map(
  (type) => adminSchemas[type]
) as [ReturnType<typeof makeAdminSchema>, ...ReturnType<typeof makeAdminSchema>[]];

export const anyAdminSchema = z.discriminatedUnion("type", adminSchemasArray);

type AdminPersistedFields = {
  patient: Patient | null;
  orderingProvider: Practitioner | null;
};

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Admin = Omit<
  ExtractedDocumentFields<AdminDocumentType, typeof extendedAdminSchema, typeof baseAdminFields>,
  keyof AdminPersistedFields
> &
  AdminPersistedFields &
  DatabaseDefaults;

const now = new Date().toISOString();

export const adminShape: Admin = {
  id: "",
  type: AdminDocumentType.ADMIN,
  patient: null,
  receivingLocation: null,
  orderingProvider: null,
  authorizationStatus: null,
  companyName: null,
  documentType: null,
  facilityType: null,
  clinicCallReason: null,
  equipmentType: null,
  isSigned: null,
  nameOfDocument: null,
  schedulingPriority: null,
  specialtyOrFacilityName: null,
  equipmentOrSupply: null,
  nameOfIllness: null,
  patientLogType: null,
  signatureRequired: null,
  isScored: null,
  signaturePageNumber: null,
  disabilityDurationType: null,
  dateOfDocument: null,
  dateReceived: null,
  dateOfEvent: null,
  dateSigned: null,
  receivingProvider: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
